/* eslint-disable react/no-array-index-key */
import React, { useContext, useState } from 'react';
import Button from '@material-ui/core/Button';
import {
  buildHCPCS,
  addDuration,
  icdCodeNamesToMap,
} from '@mummssoftware/utils/hcpcsUtils';
import { canRecordHcpcs } from '@mummssoftware/utils/hcpcsValidationUtils';
import { PalliativeCare } from '@mummssoftware/utils/constants';
import { HCPCSContext } from '../Context/contexts/HCPCSContext';
import { VisitContext } from '../Context/contexts/VisitContext';
import {
  SET_SHOW_HCPCS,
  RESET_HCPCS,
  SHOW_VISIT_LOADER,
} from '../Context/constants';
import { Context } from '../../context';
import { SubmitHCPCSDiv } from '../../components';
import defaults from '../../translations/en.json';
import { useStyles } from './style';

export const SubmitHCPCS = () => {
  const classes = useStyles();
  const { patient } = useContext(Context);
  const { savedVisit, updatePatientVisit, stopVisit } = useContext(
    VisitContext,
  );
  const {
    submithcpcsError,
    dispatchHCPCS,
    hcpcsCodesMap,
    hcpcsDuration,
    hcpcsMapping,
    icdCodeNames,
    icdSection,
    showVisitLoader,
    recordData,
  } = useContext(HCPCSContext);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isPalCarePatient = patient?.programSiteName === PalliativeCare;

  const canSaveHCPCS = canRecordHcpcs({
    hcpcsMapping,
    hcpcsDuration,
    icdCodeNames,
    icdSection,
    isPalCarePatient,
  });

  const cancelHCPCS = () => {
    setSaveError(null);
    dispatchHCPCS({
      type: SET_SHOW_HCPCS,
      payload: false,
    });
    dispatchHCPCS({
      type: RESET_HCPCS,
    });
  };

  const saveHCPCS = async () => {
    if (!patient || !savedVisit) {
      return;
    }

    setSaveError(null);
    dispatchHCPCS({
      type: SHOW_VISIT_LOADER,
      payload: true,
    });

    const visitToSave = { ...savedVisit };
    // payload does not have endDate => 400 error
    if (visitToSave.endDate) {
      delete visitToSave.endDate;
    }
    if (hcpcsDuration) {
      const hcpcsallduration = addDuration(Object.values(hcpcsDuration));
      if (hcpcsallduration > visitToSave.duration) {
        visitToSave.duration = hcpcsallduration;
      }
    }

    const hcpcs: any = buildHCPCS(
      hcpcsCodesMap,
      hcpcsDuration,
      icdCodeNamesToMap(icdCodeNames),
      patient?.currentFacilityId,
      patient?.currentFacilityType,
    );

    // If build fails (e.g. empty hcpcsCodesMap after reload), do not PUT the visit
    // with stale savedVisit.physicianServices (old diagnoses only).
    if (!hcpcs) {
      dispatchHCPCS({
        type: SHOW_VISIT_LOADER,
        payload: false,
      });
      setSaveError(defaults.visitSaveHCPCSError);
      return;
    }

    const saved = await updatePatientVisit(
      (patient as mumms.patient).patientNumber,
      visitToSave,
      hcpcs,
    );

    dispatchHCPCS({
      type: SHOW_VISIT_LOADER,
      payload: false,
    });

    if (!saved) {
      setSaveError(defaults.visitSaveHCPCSError);
      return;
    }

    setSaveError(null);
    dispatchHCPCS({
      type: SET_SHOW_HCPCS,
      payload: false,
    });
    dispatchHCPCS({
      type: RESET_HCPCS,
    });
  };

  return (
    <div className={classes.submitFooter}>
      {saveError ? (
        <p className={classes.saveError} role="alert">
          {saveError}
        </p>
      ) : null}
      <SubmitHCPCSDiv record>
        <Button
          title={defaults.cancel}
          size="small"
          variant="contained"
          color="primary"
          onClick={cancelHCPCS}
          disabled={showVisitLoader}
        >
          {defaults.cancel}
        </Button>
        <Button
          title={defaults.recordHCPCS}
          size="small"
          variant="contained"
          color="primary"
          onClick={saveHCPCS}
          disabled={
            showVisitLoader ||
            stopVisit ||
            submithcpcsError ||
            !canSaveHCPCS ||
            !recordData
          }
        >
          {defaults.recordHCPCS}
        </Button>
      </SubmitHCPCSDiv>
    </div>
  );
};
